import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IconButton, Popover, Box, Typography, Stack, Chip, Skeleton,
  Tooltip,
} from '@mui/material';
import {
  TipsAndUpdatesOutlined as LightbulbIcon,
  OpenInNew as LinkIcon,
  MenuBook as GuideIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { aiAnalyticsApi } from '@/api/endpoints/aiAnalytics';
import { useUIStore } from '@/stores/uiStore';
import { TYPE } from '@/theme/typography';

interface PageHelpIconProps {
  pageKey: string;
  size?: 'small' | 'medium';
}

/**
 * Contextual help icon that shows page-specific help content.
 * Place next to page titles. Content is seeded in the page_help table.
 */
export default function PageHelpIcon({ pageKey, size = 'small' }: PageHelpIconProps) {
  const showPageHelp = useUIStore((s) => s.showPageHelp);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const { data: help, isLoading } = useQuery({
    queryKey: ['page-help', pageKey],
    queryFn: () => aiAnalyticsApi.getPageHelp(pageKey).then((r) => r.data),
    enabled: open && showPageHelp,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  /** Convert **bold** markers to <strong> tags, sanitize to prevent XSS */
  const formattedContent = useMemo(() => {
    if (!help?.content) return '';
    const raw = help.content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    return DOMPurify.sanitize(raw, { ALLOWED_TAGS: ['strong', 'em', 'br', 'p'] });
  }, [help?.content]);

  if (!showPageHelp) return null;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const pageKeyToPath = (key: string) => {
    return '/' + key.replace(/\./g, '/');
  };

  /** Map pageKey to user guide slug. e.g. "hr.employees" → "hr".
   *  Points at the in-app guide viewer route so users land on a rendered
   *  page with navigation, not a raw .md download. */
  const guideFile = pageKey.split('.')[0];
  const guideUrl = `/help/${guideFile}`;

  return (
    <>
      <Tooltip title="Page info">
        <IconButton onClick={handleClick} size={size} aria-label="Page help" sx={{ color: 'warning.main', minWidth: { xs: 44, md: 'auto' }, minHeight: { xs: 44, md: 'auto' } }}>
          <LightbulbIcon fontSize={size} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { maxWidth: 420, p: 2.5, borderRadius: 2 },
        }}
      >
        {isLoading ? (
          <Box>
            <Skeleton width="60%" height={21} />
            <Skeleton width="100%" height={14} sx={{ mt: 1 }} />
            <Skeleton width="90%" height={14} />
            <Skeleton width="70%" height={14} />
          </Box>
        ) : help ? (
          <Box>
            <Typography sx={{ fontSize: TYPE.base, fontWeight: TYPE.semibold, mb: 0.5 }}>
              {help.title}
            </Typography>
            <Typography
              component="div"
              color="text.secondary"
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: TYPE.xs }}
              dangerouslySetInnerHTML={{ __html: formattedContent }}
            />

            {help.related_pages && help.related_pages.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: TYPE.xs }}>
                  Related pages
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {help.related_pages.map((rp) => (
                    <Chip
                      key={rp.key}
                      label={rp.label}
                      size="small"
                      variant="outlined"
                      clickable
                      icon={<LinkIcon sx={{ fontSize: 12 }} />}
                      onClick={() => {
                        handleClose();
                        navigate(pageKeyToPath(rp.key));
                      }}
                      sx={{ fontSize: TYPE.xs, height: 24 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Chip
              label="Read the guide"
              size="small"
              icon={<GuideIcon sx={{ fontSize: 14 }} />}
              clickable
              color="primary"
              variant="outlined"
              onClick={() => {
                handleClose();
                window.open(guideUrl, '_blank');
              }}
              sx={{ mt: 2, fontSize: TYPE.xs, height: 26 }}
            />
          </Box>
        ) : (
          <Box>
            <Typography color="text.secondary" sx={{ fontSize: TYPE.xs, mb: 1.5 }}>
              No help content loaded for this page.
            </Typography>
            <Chip
              label="Read the guide"
              size="small"
              icon={<GuideIcon sx={{ fontSize: 14 }} />}
              clickable
              color="primary"
              variant="outlined"
              onClick={() => {
                handleClose();
                window.open(guideUrl, '_blank');
              }}
              sx={{ fontSize: TYPE.xs, height: 26 }}
            />
          </Box>
        )}
      </Popover>
    </>
  );
}
